<script lang="ts">
  import { CirclePlus, Trash } from "lucide-svelte"
  import { fade } from "svelte/transition"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  let props = $props()
  let alignment_only_members: UserType[] = $state(
    props.alignment_only_members || []
  )
  let onupdateMembers: (data: any) => void = props.onupdateMembers

  let _users: UserType[] = Object.values(appStore.users).filter(
    (u: UserType) => !u.deleted && u.role !== "coach"
  )

  let newMember: any = {
    name: "",
    email: ""
  }

  let showAddMember: boolean = $state(false)

  function enableAddMember() {
    newMember.name = ""
    newMember.email = ""
    showAddMember = true
  }

  function cancelAddMember() {
    newMember.name = ""
    newMember.email = ""
    showAddMember = false
  }

  let errors = {}
  let saving = false
  async function addAlignmentMember() {
    errors = {}

    if (newMember.name.length == 0 || newMember.email.length == 0) {
      alert("Please enter both name and email for new member")
      return
    }

    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/admin/alignment_test/add_member`,
        {
          user: {
            name: newMember.name,
            email: newMember.email
          }
        }
      )

      alignment_only_members = [resp.data, ...alignment_only_members]
      onupdateMembers(alignment_only_members)
    } catch (e) {
      toast("There was an error adding the new member", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      showAddMember = false
    }
  }

  async function deleteAlignmentMember(id) {
    errors = {}

    try {
      const resp = await xhr.delete(
        `/plans/${appStore.plan.id}/admin/alignment_test/${id}/delete_member`
      )

      const idx = alignment_only_members.findIndex((d) => d.id === id)
      alignment_only_members.splice(idx, 1)
      alignment_only_members = alignment_only_members
      onupdateMembers(alignment_only_members)
    } catch (e) {
      toast("There was an error creating the new member", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      showAddMember = false
    }
  }
</script>

<div class="relative">
  <div class="grid grid-cols-1 mb-8 border-b border-gray-200 md:py-3 md:px-3">
    <div>
      <h3 class="text-lg md:text-2xl font-semibold leading-6 text-gray-900">
        Participants
      </h3>
    </div>
    <div>
      <p class="mt-4 text-base text-gray-600">
        The following shows all possible participants that can be invited to an
        alignment test. You can add additional Alignment-Only Participants here
        at no extra cost. To add additional Plan Members, please do so from your
        Plan Admin panel. Adding additional Plan Members may require a
        subscription increase.
      </p>
    </div>
  </div>

  {#if showAddMember}
    <div
      class="bg-zinc-50 max-w-3xl mx-auto m-4 p-6 border-zinc-200 rounded"
      transition:fade
    >
      <p class="text-lg font-bold mb-4">New Alignment-Only Member Detail</p>

      <div class="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
        <div>
          <label
            for="name"
            class="block text-sm font-medium leading-6 text-gray-900"
            >Full name</label
          >
          <div class="mt-2">
            <input
              type="text"
              id="name"
              bind:value={newMember.name}
              class="input block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <label
            for="email"
            class="block text-sm font-medium leading-6 text-gray-900"
            >Email address</label
          >
          <div class="mt-2">
            <input
              id="email"
              bind:value={newMember.email}
              autocomplete="email"
              class="input block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>
      </div>
      <div class="mt-6 flex items-center justify-end gap-x-6">
        {#if saving}
          <span
            class="rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
            >Saving...</span
          >
        {:else}
          <button
            type="button"
            onclick={cancelAddMember}
            class="text-sm font-semibold leading-6 text-gray-900">Cancel</button
          >
          <button
            type="submit"
            onclick={addAlignmentMember}
            class="rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
            >Save</button
          >
        {/if}
      </div>
    </div>
  {:else}
    <div class="border border-zinc-200 bg-zinc-50 rounded py-6 px-8 mt-6">
      <div
        class="grid grid-cols-3 mb-8 border-b border-gray-200 md:py-3 md:px-3"
      >
        <div class="col-span-2">
          <p class="text-xl font-bold mb-4">Alignment-Only Participants</p>
        </div>
        <div class="flex justify-end items-start">
          {#if !showAddMember}
            <button
              type="button"
              class="inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
              onclick={enableAddMember}
            >
              Add Participant
              <CirclePlus class="h-6 w-6" />
            </button>
          {/if}
        </div>
      </div>
      <ul role="list" class="divide-y divide-gray-100 space-y-3">
        {#each alignment_only_members as member (member.id)}
          <li class="relative hover:bg-gray-50">
            <div
              class="grid grid-cols-3 px-4 py-3 sm:px-6 lg:px-8 p-2 bg-white rounded-sm border border-zinc-200 shadow"
            >
              <div class="flex items-center">
                <p class="text-base font-semibold leading-6 text-gray-900">
                  {member.name}
                </p>
              </div>
              <div class="flex items-center">
                <p class="mt-1 flex text-xs leading-5 text-gray-500">
                  <span
                    class="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20"
                  >
                    {member.email}
                  </span>
                </p>
              </div>

              <div
                class="flex flex-col justify-end items-center gap-x-4 gap-y-4"
              >
                <button
                  onclick={() => deleteAlignmentMember(member.id)}
                  class=" bg-red-200 py-2 px-3 text-xs flex items-center text-red-700 gap-x-2 rounded shadow hover:bg-red-100"
                >
                  Delete
                  <Trash class="w-4 h-4 flex-none text-red-700" />
                </button>
              </div>
            </div>
          </li>
        {:else}
          <li
            class="relative flex justify-between gap-x-6 px-4 py-5 font-bold sm:px-6 lg:px-8"
          >
            No Alignment-Only Members
          </li>
        {/each}
      </ul>
    </div>

    <div class="border border-zinc-200 bg-zinc-50 rounded py-6 px-8 mt-6">
      <p class="text-xl font-bold mb-4">Plan Members</p>
      <ul role="list" class="divide-y divide-gray-200">
        {#each _users as user (user.id)}
          <li class="relative">
            <div class="grid grid-cols-3 gap-x-4 px-4 py-4 sm:px-6 lg:px-8">
              <div class="flex min-w-0 gap-x-4">
                <div class="min-w-0 flex-auto">
                  <p class="text-base font-semibold leading-6 text-gray-900">
                    {user.name}
                  </p>
                </div>
              </div>

              <div class="flex items-center justify-start">
                <div
                  class="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20"
                >
                  <span class="font-sm font-bold">{user.email}</span>
                </div>
              </div>
              <div class="flex items-center justify-start">
                <div
                  class="inline-flex items-center rounded-md bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 ring-1 ring-inset ring-teal-600/20"
                >
                  <span class="font-sm font-bold">{user.role}</span>
                </div>
              </div>
            </div>
          </li>
        {:else}
          <li
            class="relative flex justify-between gap-x-6 px-4 py-5 hover:bg-gray-50 sm:px-6 lg:px-8"
          >
            No Plan Members
          </li>
        {/each}
      </ul>
    </div>
  {/if}
</div>
